import { useEffect, useMemo, useState } from "react"
import usePrevious from "hooks/usePrevious"
import { ApplicationModal } from "state/application/actions"
import { useModalOpen, useWalletModalToggle } from "state/application/hooks"
import ArrowRight from "assets/images/arrow-right.svg"
import { useThePoolz, useSetProvider, useSyncProviders } from "@poolzfinance/reacthelper"
import Loader from "components/Loader"
import ModalWindow from "components/ModalWindow/ModalWindow"
import { useLazyMyRoles } from "@poolzfinance/api4"
import { useDefaultWallets } from "@poolzfinance/strapi"

const WalletModal = () => {
  const setProvider = useSetProvider()
  const thePoolz = useThePoolz()
  const providers = useSyncProviders()

  const { web3, account } = thePoolz
  const [walletView, setWalletView] = useState("")

  const walletModalOpen = useModalOpen(ApplicationModal.WALLET)
  const toggleWalletModal = useWalletModalToggle()

  const previousAccount = usePrevious(account)

  const [loadMyRoles] = useLazyMyRoles()

  const { data: defaultWalletsStrapi } = useDefaultWallets()
  useEffect(() => {
    if (account && !previousAccount && walletModalOpen) {
      toggleWalletModal()
      loadMyRoles()
    }
  }, [account, previousAccount, toggleWalletModal, walletModalOpen, loadMyRoles])

  useEffect(() => {
    if (walletModalOpen) {
      setWalletView("")
    }
  }, [walletModalOpen])

  const installWallet = useMemo(() => {
    return (defaultWalletsStrapi?.defaultWallets ?? [])
      .filter((wallet) => wallet !== null)
      .filter(({ Name }) => !providers.find(({ info }) => new RegExp(Name, "i").test(info.name)))
  }, [providers, defaultWalletsStrapi])

  return (
    <ModalWindow isOpen={walletModalOpen} closeModal={toggleWalletModal} parentSelector="#root">
      <div data-testid="wallet-modal-id" className="flex flex-col flex-nowrap w-full md:min-w-[450px]">
        <div className="relative p-4 flex flex-col gap-y-8">
          <div>
            <span className="text-[2rem] text-transparent bg-clip-text bg-gradient-to-r from-[#0666EA] to-[#FB71AE]">
              Connect
            </span>
          </div>
          <div data-testid="option-grid-id" className="flex flex-col gap-y-2 w-full">
            {providers.map(({ info, provider }) => {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              const selectedWallet = account && web3 && web3.currentProvider === provider
              const walletName = info.name //.replace(/wallet/i, "")

              if (selectedWallet) return <h1 key={info.name}>Selected {info.name}</h1>

              return (
                <button
                  key={info.name}
                  onClick={async () => {
                    setWalletView(info.name)
                    try {
                      await provider.request({ method: "eth_requestAccounts" })
                      setProvider(provider)
                    } catch (e) {
                      console.error(e)
                    }
                  }}
                  className="flex flex-row justify-between items-center rounded-md bg-[#010013] h-14 px-4 w-full">
                  <div className="text-white">{walletName}</div>
                  <div>
                    {walletView === info.name ? (
                      <Loader />
                    ) : (
                      <img src={info.icon} alt={info.name} className="w-6 h-6" />
                    )}
                  </div>
                </button>
              )
            })}

            {installWallet.map(({ Name, Link, Icon }) => (
              <button
                key={Name}
                className="flex flex-row justify-between items-center rounded-md bg-[#010013] h-14 px-4 w-full"
                onClick={() => window.open(Link, "_blank", "noreferrer noopener")}>
                <div className="text-white">Install {Name} Wallet</div>
                <div>
                  <img src={Icon.url} alt={Name} className="w-6 h-6" />
                </div>
              </button>
            ))}
          </div>
          <div data-testid="external-wallet-link-id" className="flex justify-between items-center">
            <div className="text-[11px] text-[#9e9cba]">First time setting up a wallet?</div>
            <a href="https://ethereum.org/wallets/" target="_blank" rel="noopener noreferrer">
              <img src={ArrowRight} alt="Arrow" />
            </a>
          </div>
        </div>
      </div>
    </ModalWindow>
  )
}

export default WalletModal
